import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PromoBanner } from '../../types';
import { resolveMediaUrl } from '../../utils/api';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PromoBannerCarouselProps {
  banners: PromoBanner[];
  autoSlideInterval?: number; // ms, default 4500 (4.5 seconds)
  className?: string;
  aspectRatioClass?: string;
  showControls?: boolean;
  campaignLabel?: string;
  defaultTitle?: string;
  defaultSubtitle?: string;
  onBannerClick?: (banner: PromoBanner) => void;
  onOpenOrder?: (banner: PromoBanner) => void;
  onViewMenu?: () => void;
}

export const PromoBannerCarousel: React.FC<PromoBannerCarouselProps> = ({
  banners,
  autoSlideInterval = 4500,
  className = '',
  aspectRatioClass = 'aspect-[16/10] sm:aspect-[21/9]',
  showControls = true,
  onBannerClick,
}) => {
  const activeBanners = banners && banners.length > 0 ? banners : [];
  const totalSlides = activeBanners.length;

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (currentIndex >= totalSlides && totalSlides > 0) {
      setCurrentIndex(0);
    }
  }, [totalSlides, currentIndex]);

  const goToNext = useCallback(() => {
    if (totalSlides <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const goToPrev = useCallback(() => {
    if (totalSlides <= 1) return;
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  const goToSlide = (index: number) => {
    if (index >= 0 && index < totalSlides) {
      setCurrentIndex(index);
    }
  };

  useEffect(() => {
    if (totalSlides <= 1 || isPaused) return;

    timerRef.current = setInterval(() => {
      goToNext();
    }, autoSlideInterval);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [totalSlides, isPaused, autoSlideInterval, goToNext]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches && e.touches[0]) {
      setTouchStartX(e.touches[0].clientX);
      setTouchStartY(e.touches[0].clientY);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null || touchStartY === null || totalSlides <= 1) {
      setTouchStartX(null);
      setTouchStartY(null);
      return;
    }

    const touchEndX = e.changedTouches[0]?.clientX ?? touchStartX;
    const touchEndY = e.changedTouches[0]?.clientY ?? touchStartY;

    const deltaX = touchEndX - touchStartX;
    const deltaY = touchEndY - touchStartY;

    if (Math.abs(deltaX) > 35 && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX < 0) {
        goToNext();
      } else {
        goToPrev();
      }
    }

    setTouchStartX(null);
    setTouchStartY(null);
  };

  if (totalSlides === 0) {
    return null;
  }

  return (
    <div
      className={`relative w-full overflow-hidden bg-[#f6f3ee] select-none group ${className}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className={`relative w-full ${aspectRatioClass} overflow-hidden`}>
        {activeBanners.map((banner, index) => {
          const isActive = index === currentIndex;
          const isNext = index === (currentIndex + 1) % totalSlides;
          const isPrev = index === (currentIndex - 1 + totalSlides) % totalSlides;
          const isVisible = isActive || isNext || isPrev;

          const imageUrl =
            resolveMediaUrl(banner.imageUrl) || '/assets/leton-coffee-beans.jpg';

          return (
            <div
              key={banner.id || `banner-slide-${index}`}
              onClick={() => onBannerClick && onBannerClick(banner)}
              className={`inset-0 w-full h-full transition-opacity duration-500 ease-out ${
                onBannerClick ? 'cursor-pointer' : ''
              } ${
                isActive
                  ? 'relative opacity-100 z-10'
                  : 'absolute opacity-0 z-0 pointer-events-none'
              }`}
              style={{
                pointerEvents: isActive ? 'auto' : 'none',
              }}
            >
              {/* Pure Unobstructed Banner Photo (No title, subtitle, badge, buttons, or dark overlay) */}
              {isVisible && (
                <img
                  src={imageUrl}
                  alt={banner.title || `LetonKu Banner ${index + 1}`}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  {...(index === 0 ? { fetchPriority: 'high' } : { fetchPriority: 'low' })}
                  className="w-full h-full object-cover object-center transition-transform duration-700"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    const target = e.currentTarget;
                    if (
                      target.src !== '/assets/leton-coffee-beans.jpg' &&
                      !target.src.endsWith('/assets/leton-coffee-beans.jpg')
                    ) {
                      target.src = '/assets/leton-coffee-beans.jpg';
                    }
                  }}
                />
              )}
            </div>
          );
        })}

        {/* Minimal Carousel Pagination Dots */}
        {totalSlides > 1 && (
          <div className="absolute bottom-9 sm:bottom-11 left-4 sm:left-6 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/25 backdrop-blur-xs">
            {activeBanners.map((_, dotIdx) => {
              const isCurrent = dotIdx === currentIndex;
              return (
                <button
                  key={`dot-${dotIdx}`}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToSlide(dotIdx);
                  }}
                  aria-label={`Pilih slide ${dotIdx + 1}`}
                  className={`transition-all duration-300 rounded-full cursor-pointer ${
                    isCurrent
                      ? 'w-5 h-1.5 bg-white'
                      : 'w-1.5 h-1.5 bg-white/55 hover:bg-white/80'
                  }`}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Desktop Prev / Next Navigation Arrows */}
      {showControls && totalSlides > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goToPrev();
            }}
            aria-label="Slide Sebelumnya"
            className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white/85 hover:bg-white text-[#0b1c30] shadow-md items-center justify-center transition-all opacity-0 group-hover:opacity-100 focus:opacity-100 active:scale-95 cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            aria-label="Slide Berikutnya"
            className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white/85 hover:bg-white text-[#0b1c30] shadow-md items-center justify-center transition-all opacity-0 group-hover:opacity-100 focus:opacity-100 active:scale-95 cursor-pointer"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  );
};
